// src/receiptReader.js
// ─────────────────────────────────────────────────────────────
// Receipt files (PDF printouts of receipt emails, scans, phone photos) →
// what Claude reads off them, for the expense importer (10/9/26).
//
//   readReceiptFile(file, getToken, opts) → {receipts, usage, pages, requests} | {error}
//   readReceiptFiles(files, opts)          → [{file, info, result}]  (3 at a time)
//   receiptFileInfo(file)                  → {name, type, size, lastModified, hash, fileRef}
//   countReceiptPages(file) / estimateReceiptCost(pageCounts) → shown BEFORE reading
//
// The server side is action "receipt-read" on /api/anthropic-proxy: the
// server builds the whole request (Haiku 4.5, fixed prompt, ≤ 2000 output
// tokens); this file only sends the bytes. A file that fits in one request
// (~3.5 MB of base64, under Vercel's ~4.5 MB body cap) goes as-is; a bigger
// PDF is rendered page by page with pdf.js (kb.js's loader) to JPEGs and sent
// in page groups that each stay under the cap, and the answers are merged;
// a big photo is shrunk on a canvas first.
//
// CSP (vercel.json): never fetch() a data: URL — base64 comes from
// FileReader / ArrayBuffer (kb.js blobToBase64), never the other way round.
//
// Cost: Haiku 4.5 is $1 / $5 per million input / output tokens; a PDF page
// is ~1,600 input tokens, so a 3-page receipt printout is well under 1¢.
// ─────────────────────────────────────────────────────────────

import { getAccessToken, openPdf, renderPdfPage, prepareOcrImage, blobToBase64, readBlobArrayBuffer } from './kb';

export const RECEIPT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const PROXY_URL = "/api/anthropic-proxy";
const ACTION = "receipt-read";

export const RECEIPT_TOKENS_PER_PAGE = 1600;   // a PDF page or a rendered page image
const PROMPT_TOKENS = 900;                     // the fixed system prompt + ask, per request
const OUTPUT_TOKENS_PER_FILE = 450;            // one receipt's JSON, roughly
const PRICE_IN = 1 / 1e6;                      // Haiku 4.5, $ per input token
const PRICE_OUT = 5 / 1e6;                     // Haiku 4.5, $ per output token

const MAX_REQUEST_B64 = 3500000;   // per request (file or page group); the server allows 4.4 MB
const GROUP_PAGES = 8;             // rendered pages per request
const MAX_PAGES = 60;              // pages read from one PDF (a receipt pile, not a book)
const RENDER_WIDTH = 1200;         // px — a letter page then sits near Claude's 1,568 px long edge
const RENDER_QUALITY = 0.8;
const CONCURRENCY = 3;
const REQUEST_TIMEOUT_MS = 90000;  // the function itself is capped at 60 s
const RETRY_DELAY_MS = 2000;
const RATE_LIMIT_DELAY_MS = 6000;

function sleep(ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); }
function errText(e) { return (e && e.message) || String(e || "unknown error"); }
function base64Len(bytes) { return Math.ceil((Number(bytes) || 0) / 3) * 4; }

// "pdf" | "image" | null
export function receiptKindOf(file) {
  const name = String((file && file.name) || "").toLowerCase();
  const type = String((file && file.type) || "").toLowerCase();
  if (type === "application/pdf" || /\.pdf$/.test(name)) return "pdf";
  if (/^image\//.test(type) || /\.(jpe?g|png|webp)$/.test(name)) return "image";
  return null;
}
export function isReceiptFile(file) { return receiptKindOf(file) !== null; }

// SHA-256 of the file's bytes (hex) — the receipt's identity: the same file
// dropped twice, or again next month, is recognised. Falls back to a
// non-crypto 64-bit hash where crypto.subtle isn't available (plain-http dev).
export async function sha256Hex(fileOrBuffer) {
  const buf = fileOrBuffer instanceof ArrayBuffer ? fileOrBuffer : await readBlobArrayBuffer(fileOrBuffer);
  const subtle = typeof window !== "undefined" && window.crypto && window.crypto.subtle;
  if (subtle && typeof subtle.digest === "function") {
    try {
      const d = new Uint8Array(await subtle.digest("SHA-256", buf));
      let hex = "";
      for (let i = 0; i < d.length; i++) hex += d[i].toString(16).padStart(2, "0");
      return hex;
    } catch (_e) { /* fall through */ }
  }
  const u8 = new Uint8Array(buf);
  let h1 = 0x811c9dc5, h2 = 0x01000193 ^ u8.length;
  for (let i = 0; i < u8.length; i++) {
    h1 = Math.imul(h1 ^ u8[i], 16777619);
    h2 = Math.imul(h2 ^ u8[i], 2246822519);
  }
  return "f" + (h1 >>> 0).toString(16).padStart(8, "0") + (h2 >>> 0).toString(16).padStart(8, "0") + u8.length.toString(16);
}

// What the importer needs to know about a receipt file (fileRef = the key
// the UI uses to find the File again when uploading it as the receipt).
export async function receiptFileInfo(file) {
  const hash = await sha256Hex(file);
  return {
    name: (file && file.name) || "receipt",
    type: (file && file.type) || "",
    size: (file && file.size) || 0,
    lastModified: (file && file.lastModified) || null,
    hash,
    fileRef: hash,
  };
}

// Pages a file will be read as: a PDF's page count (pdf.js), 1 for a photo.
export async function countReceiptPages(file) {
  if (receiptKindOf(file) !== "pdf") return 1;
  let pdf = null;
  try {
    pdf = await openPdf(await readBlobArrayBuffer(file));
    return Math.max(1, Math.min(MAX_PAGES, pdf.numPages || 1));
  } catch (_e) {
    return 1;
  } finally {
    if (pdf) { try { pdf.destroy(); } catch (_e) { /* gone */ } }
  }
}

// pageCounts: one number per file (or one total page count). →
// {files, pages, inputTokens, outputTokens, dollars, label}
export function estimateReceiptCost(pageCounts) {
  const list = Array.isArray(pageCounts) ? pageCounts : [Number(pageCounts) || 0];
  const files = Array.isArray(pageCounts) ? list.length : (list[0] > 0 ? 1 : 0);
  const pages = list.reduce(function (s, n) { return s + Math.max(1, Number(n) || 1); }, 0);
  const requests = list.reduce(function (s, n) { return s + Math.max(1, Math.ceil((Number(n) || 1) / GROUP_PAGES)); }, 0);
  const inputTokens = pages * RECEIPT_TOKENS_PER_PAGE + requests * PROMPT_TOKENS;
  const outputTokens = Math.max(files, 1) * OUTPUT_TOKENS_PER_FILE;
  const dollars = files ? inputTokens * PRICE_IN + outputTokens * PRICE_OUT : 0;
  return { files, pages, inputTokens, outputTokens, dollars, label: fmtCost(dollars) };
}
export function fmtCost(dollars) {
  const d = Number(dollars) || 0;
  if (d <= 0) return "$0";
  if (d < 0.01) return "under 1¢";
  if (d < 1) return "about " + Math.round(d * 100) + "¢";
  return "about $" + d.toFixed(2);
}
// Real spend from the usage the server sent back.
export function costOfUsage(usage) {
  const u = usage || {};
  return (Number(u.input_tokens) || 0) * PRICE_IN + (Number(u.output_tokens) || 0) * PRICE_OUT;
}

// ── HTTP ─────────────────────────────────────────────────────

function messageFor(status, data) {
  const code = String((data && (data.error || data.code)) || "");
  const detail = data && typeof data.detail === "string" ? data.detail : "";
  // An older deployed proxy: no such action (Reece) / not on Max's list yet (Max).
  if (code === "unknown_action" || code === "reece_only") return "the deployed server doesn't have the receipt reader yet — deploy the updated api/anthropic-proxy.js";
  if (status === 401) return "your session has expired — sign out, sign back in, and try again";
  if (status === 403) return "this login isn't allowed to read receipts";
  if (status === 404) return "the receipt reader isn't on this server — it runs on the deployed site (or `vercel dev`), not plain `npm start`";
  if (status === 413) return "the file was too large for the server";
  if (code === "anthropic_key_not_configured") return "the Anthropic API key (ANTHROPIC_KEY) isn't set on the server";
  if (status === 429) return "Claude is rate-limiting requests — wait a minute and try again";
  if (detail) return detail;
  return "the server answered " + status + (code ? " (" + code + ")" : "");
}

async function postOnce(body, token) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? setTimeout(function () { ctl.abort(); }, REQUEST_TIMEOUT_MS) : null;
  try {
    let resp;
    try {
      resp = await fetch(PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
        body: JSON.stringify(body),
        signal: ctl ? ctl.signal : undefined,
      });
    } catch (e) {
      return { network: true, error: (e && e.name === "AbortError") ? "the request timed out" : "couldn't reach the server (" + errText(e) + ")" };
    }
    let raw = "";
    try { raw = await resp.text(); } catch (_e) { raw = ""; }
    let data = null;
    try { data = raw ? JSON.parse(raw) : null; } catch (_e) { data = null; }
    return { status: resp.status, ok: resp.ok, data };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// kb.getAccessToken's errors are worded for the Lender Library; this text
// ends up in the expense's "Couldn't read this receipt (…)" flag, so say
// what it means for a receipt.
function sessionMessage(e) {
  const m = errText(e);
  if (/needs the supabase database|isn't connected here/i.test(m)) return "the receipt reader needs the cloud database, and it isn't connected here (local mode)";
  if (/not signed in/i.test(m)) return "you're not signed in — sign out, sign back in, and try again";
  return m.replace(/ to use the Lender Library/i, "");
}

// One request, retried once on a network error / 5xx / 429.
// ctx: {getToken, run, retryDelayMs}. → {receipts, usage} | {error, usage?, code?}
async function callReader(body, ctx) {
  let token;
  try { token = await (ctx.getToken || getAccessToken)(); } catch (e) { return { error: sessionMessage(e), code: "no_session" }; }
  for (let attempt = 0; ; attempt++) {
    const r = await postOnce(body, token);
    const retryable = r.network || r.status >= 500 || r.status === 429;
    if (retryable && attempt === 0) { await sleep(ctx.retryDelayMs != null ? ctx.retryDelayMs : r.status === 429 ? RATE_LIMIT_DELAY_MS : RETRY_DELAY_MS); continue; }
    if (r.network) return { error: r.error, code: "network" };
    const data = r.data || {};
    if (!r.ok) return { error: messageFor(r.status, data), code: String(data.error || r.status), status: r.status, usage: data.usage || null };
    if (data.error) return { error: String(data.error), code: "unparsed", usage: data.usage || null };
    if (!Array.isArray(data.receipts)) return { error: "the server sent back an unreadable answer", code: "bad_response" };
    return { receipts: data.receipts, usage: data.usage || null };
  }
}

// Run tasks with at most `n` in flight.
function limiter(n) {
  let active = 0;
  const queue = [];
  const next = function () {
    if (active >= n || !queue.length) return;
    active++;
    const job = queue.shift();
    job.fn().then(job.resolve, job.reject).finally(function () { active--; next(); });
  };
  return function (fn) {
    return new Promise(function (resolve, reject) { queue.push({ fn, resolve, reject }); next(); });
  };
}

function addUsage(a, b) {
  return {
    input_tokens: ((a && a.input_tokens) || 0) + ((b && b.input_tokens) || 0),
    output_tokens: ((a && a.output_tokens) || 0) + ((b && b.output_tokens) || 0),
  };
}
// The page groups' answers, in page order, as one list. Nothing is ever
// dropped: two receipts with the same vendor, total and date inside ONE
// group are two purchases (several same-day PropertyShark reports). The
// same receipt in two NEIGHBOURING groups is either a receipt cut across
// the page break or two identical purchases — the copy is dropped only
// when both carry the same order / transaction number (then it is one
// receipt); otherwise both stay and the second is marked possibleRepeat
// (the importer flags it, so Reece decides).
function mergeReceipts(lists) {
  const sig = function (r) { return [String(r.vendor || "").toLowerCase(), r.total, r.date].join("|"); };
  const txn = function (r) { return String(r.orderOrTransactionId || "").trim().toLowerCase(); };
  const out = [];
  let prev = [];
  lists.forEach(function (list) {
    const cur = (list || []).filter(Boolean);
    const avail = prev.slice();   // each receipt of the group before can stand for one repeat
    cur.forEach(function (r) {
      const at = r.total == null ? -1 : avail.findIndex(function (p) { return sig(p) === sig(r); });
      if (at < 0) { out.push(r); return; }
      const p = avail[at];
      avail.splice(at, 1);
      if (txn(r) && txn(r) === txn(p)) return;   // the same receipt, read in both groups
      out.push(Object.assign({}, r, { possibleRepeat: true }));
    });
    prev = cur;
  });
  return out;
}
function pageRange(from, to) { return from === to ? String(from) : from + "–" + to; }

// One page group → [{result, from, to}]. Claude's answer for a group can be
// cut off (2000 output tokens — a group of busy receipts) or come back
// unparseable: such a group is split in two and each half read again, down
// to single pages, so one crowded group doesn't lose its neighbours'
// receipts.
async function readPageGroup(file, g, total, ctx) {
  const r = await ctx.run(function () {
    return callReader({ action: ACTION, name: file.name, pages: g.pages, pageNote: "pages " + pageRange(g.from, g.to) + " of " + total + " of the file" }, ctx);
  });
  if (!(r.error && r.code === "unparsed" && g.pages.length > 1)) return [{ result: r, from: g.from, to: g.to }];
  const mid = Math.ceil(g.pages.length / 2);
  const halves = await Promise.all([
    readPageGroup(file, { pages: g.pages.slice(0, mid), from: g.from, to: g.from + mid - 1 }, total, ctx),
    readPageGroup(file, { pages: g.pages.slice(mid), from: g.from + mid, to: g.to }, total, ctx),
  ]);
  const parts = halves[0].concat(halves[1]);
  if (r.usage) parts[0] = { result: Object.assign({}, parts[0].result, { usage: addUsage(parts[0].result.usage, r.usage) }), from: parts[0].from, to: parts[0].to };
  return parts;
}

// A PDF too big for one request → its pages as JPEGs, in groups that each fit.
// → {receipts, usage, pages, requests, partial?} — partial names the pages
// that weren't read ("pages 9–16 couldn't be read"; "pages 61–80 weren't
// read (only the first 60 are)"), so the importer brings that part in as a
// $0 flagged row instead of losing it.
async function readPdfAsPages(file, ctx) {
  let pdf;
  try { pdf = await openPdf(await readBlobArrayBuffer(file)); } catch (e) { return { error: errText(e), code: "pdf" }; }
  try {
    const total = pdf.numPages || 1;
    const count = Math.min(total, MAX_PAGES);
    const groups = [];
    let cur = [], size = 0, from = 1;
    for (let p = 1; p <= count; p++) {
      const blob = await renderPdfPage(pdf, p, RENDER_WIDTH, "image/jpeg", RENDER_QUALITY);
      const data = await blobToBase64(blob);
      if (cur.length && (size + data.length > MAX_REQUEST_B64 || cur.length >= GROUP_PAGES)) {
        groups.push({ pages: cur, from, to: p - 1 });
        cur = []; size = 0; from = p;
      }
      cur.push({ mediaType: "image/jpeg", data });
      size += data.length;
    }
    if (cur.length) groups.push({ pages: cur, from, to: count });
    const nested = await Promise.all(groups.map(function (g) { return readPageGroup(file, g, total, ctx); }));
    const results = [].concat.apply([], nested);
    let usage = null;
    results.forEach(function (x) { if (x.result.usage) usage = addUsage(usage, x.result.usage); });
    const good = results.filter(function (x) { return !x.result.error; });
    const bad = results.filter(function (x) { return x.result.error; });
    if (!good.length) return { error: results[0] ? results[0].result.error : "nothing came back", usage, pages: count, requests: results.length };
    const res = { receipts: mergeReceipts(good.map(function (x) { return x.result.receipts; })), usage, pages: count, requests: results.length };
    const notes = [];
    if (bad.length) notes.push("page" + (bad.length === 1 && bad[0].from === bad[0].to ? " " : "s ") + bad.map(function (x) { return pageRange(x.from, x.to); }).join(", ") + " couldn't be read");
    if (total > count) notes.push("pages " + (count + 1) + "–" + total + " weren't read (only the first " + count + " are)");
    if (notes.length) res.partial = notes.join("; ");
    return res;
  } finally {
    try { pdf.destroy(); } catch (_e) { /* gone */ }
  }
}

// One file → {receipts, usage, pages, requests} | {error, usage?}. Never
// throws: anything that goes wrong comes back as {error} so the importer can
// bring the file in at $0, flagged ("Couldn't read this receipt …").
// opts.run: a limiter shared across files (readReceiptFiles passes one);
// opts.retryDelayMs: the pause before the one retry (tests).
export async function readReceiptFile(file, getToken, opts) {
  const o = opts || {};
  const ctx = { getToken, run: o.run || limiter(CONCURRENCY), retryDelayMs: o.retryDelayMs };
  const run = ctx.run;
  const kind = receiptKindOf(file);
  if (!kind) return { error: "not a PDF or photo" };
  try {
    if (kind === "image") {
      let img;
      try { img = await prepareOcrImage(file); } catch (e) { return { error: errText(e), code: "image" }; }
      const r = await run(function () { return callReader({ action: ACTION, name: file.name, file: { mediaType: img.media_type, data: img.data } }, ctx); });
      return r.error ? r : Object.assign(r, { pages: 1, requests: 1 });
    }
    if (base64Len(file.size) <= MAX_REQUEST_B64) {
      const data = await blobToBase64(file);
      const r = await run(function () { return callReader({ action: ACTION, name: file.name, file: { mediaType: "application/pdf", data } }, ctx); });
      // Claude couldn't open it as a PDF (damaged / over 100 pages), or its
      // answer was cut off / unparseable (too many receipts for one answer):
      // try it as page images, in groups (each group split again if needed).
      if (r.error && (r.code === "unreadable" || r.code === "unparsed")) {
        const again = await readPdfAsPages(file, ctx);
        // Both attempts were paid for: report the usage of both.
        if (!again.error) return Object.assign(again, { usage: addUsage(r.usage, again.usage), requests: (again.requests || 0) + 1 });
        if (again.usage) r.usage = addUsage(r.usage, again.usage);
      }
      return r.error ? r : Object.assign(r, { requests: 1 });
    }
    return await readPdfAsPages(file, ctx);
  } catch (e) {
    return { error: errText(e) };
  }
}

// Many files, 3 requests at a time. The same file twice (same SHA-256) is
// read once and its answer reused. onProgress({done, total, name}).
// → [{file, info, result}] in the order given.
export async function readReceiptFiles(files, opts) {
  const o = opts || {};
  const list = Array.from(files || []).filter(isReceiptFile);
  const run = limiter(o.concurrency || CONCURRENCY);       // HTTP requests in flight
  const fileRun = limiter(o.concurrency || CONCURRENCY);   // files being opened / rendered at once (memory)
  // Hash one file at a time — fifty 3 MB PDFs read all at once is a memory spike.
  const infos = [];
  for (let i = 0; i < list.length; i++) infos.push(await receiptFileInfo(list[i]));
  const byHash = new Map();
  let done = 0;
  const tick = function (name) { done++; if (o.onProgress) { try { o.onProgress({ done, total: list.length, name }); } catch (_e) { /* UI only */ } } };
  const results = await Promise.all(list.map(function (file, i) {
    const h = infos[i].hash;
    if (!byHash.has(h)) byHash.set(h, fileRun(function () { return readReceiptFile(file, o.getToken, { run, retryDelayMs: o.retryDelayMs }); }));
    return byHash.get(h).then(function (result) { tick(file.name); return { file, info: infos[i], result }; });
  }));
  return results;
}
